import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CredibilityGauge from "@/components/CredibilityGauge";
import TierBadge from "@/components/TierBadge";
import PassportCompletion from "@/components/PassportCompletion";
import ScoreBreakdown from "@/components/ScoreBreakdown";
import BadgeGrid from "@/components/BadgeGrid";
import Timeline from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Award, ScanLine, FilePlus2, ArrowUpRight, Sparkles, TrendingUp, Share2, Building2,
} from "lucide-react";import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [passport, setPassport] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [matchCount, setMatchCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        setPassport(data);
      } catch (e) { /* ignore */ }
      if (user?.role === "professional") {
        try {
          const { data } = await api.get("/claims/potential-matches");
          setMatchCount((data.matches || []).length);
        } catch (e) {}
      }
      if (user?.role === "super_admin") {
        try {
          const { data } = await api.get("/admin/stats");
          setAdminStats(data);
        } catch (e) {}
      }
    })();
  }, [user]);

  const isOrg = user?.role === "organization" || user?.role === "institution" || user?.role === "super_admin";
  const isIndividual = user?.role === "professional";
  const score = passport?.score;
  const completion = passport?.completion;
  const ranking = passport?.ranking;

  const sharePassport = async () => {
    if (!user?.ethic_id) return;
    const url = `${window.location.origin}/passport/${user.ethic_id}`;
    try {
      if (navigator.share) await navigator.share({ title: "My EthicCred Passport", url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Passport link copied");
      }
    } catch (e) { /* user cancelled */ }
  };

  const idLabel = user?.user_type === "organization" ? "Organization ID" : user?.user_type === "institution" ? "Institution ID" : "EthicCred ID";
  const idValue = user?.ethic_id || user?.org_id || user?.institution_id || "—";

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* HEADER: name + headline + ID + score + tier + ranking */}
        <div className="border border-ec-line bg-white p-6 sm:p-8 mb-8" data-testid="dashboard-header">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="eyebrow">Welcome back</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2" data-testid="dashboard-greeting">
                {user?.full_name}
              </h1>
              <p className="text-ec-ink-soft mt-1">{user?.headline || "Add a professional headline to stand out."}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="eyebrow">{idLabel}</span>
                <span className="font-mono-plex font-bold text-base" data-testid="dashboard-ethic-id">{idValue}</span>
                {isIndividual && score && (
                  <>
                    <span className="text-ec-ink-soft">·</span>
                    <span className="font-display font-bold text-base">Score {score.score}</span>
                    <TierBadge tier={score.tier} size="sm" />
                    {ranking && (
                      <span className="text-xs text-ec-ink-soft inline-flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Top {ranking.top_percent}% of {ranking.total} pros
                      </span>
                    )}
                  </>
                )}
                {!isIndividual && (
                  <>
                    <span className="text-ec-ink-soft">·</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-ec-amber border border-ec-amber/30 bg-ec-amber/10 px-2 py-0.5 rounded-full">
                      {user?.verification_status || "Unverified"}
                    </span>
                    <span className="text-xs text-ec-ink-soft">Issuer account</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {isIndividual && (
                <Link to="/passport" data-testid="dashboard-passport-link">
                  <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">
                    <Sparkles className="w-4 h-4" /> Manage Passport
                  </Button>
                </Link>
              )}
              <Link to="/certificates" data-testid="dashboard-certificates-link">
                <Button variant={isIndividual ? "outline" : "default"} className={isIndividual ? "rounded-sm border-ec-ink gap-2" : "rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2"}>
                  <Award className="w-4 h-4" /> Certificates
                </Button>
              </Link>
              {isIndividual && (
                <Button variant="ghost" className="rounded-sm gap-2" onClick={sharePassport} data-testid="dashboard-share-button">
                  <Share2 className="w-4 h-4" /> Share
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Smart match banner — INDIVIDUALS */}
        {isIndividual && matchCount > 0 && (
          <Link to="/my-records" className="block mb-8" data-testid="smart-match-banner">
            <div className="border border-ec-emerald/40 bg-ec-emerald/5 hover:bg-ec-emerald/10 transition-colors p-5 flex items-center gap-4 rounded-sm">
              <Sparkles className="w-6 h-6 text-ec-emerald flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold">We found {matchCount} record{matchCount === 1 ? "" : "s"} that may belong to you</div>
                <div className="text-sm text-ec-ink-soft mt-0.5">Open My Records to claim them and link to your Professional Passport.</div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-ec-emerald" />
            </div>
          </Link>
        )}

        {/* Super admin */}
        {adminStats && (
          <div className="mb-8">
            <div className="eyebrow mb-3">Platform stats</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                ["Users", adminStats.total_users],
                ["Organizations", adminStats.total_organizations],
                ["Institutions", adminStats.total_institutions],
                ["Certificates", adminStats.total_certificates],
                ["Verifications", adminStats.total_verifications],
              ].map(([l, v]) => (
                <Card key={l} className="rounded-sm border-ec-line">
                  <CardContent className="pt-5">
                    <div className="eyebrow">{l}</div>
                    <div className="font-display text-3xl font-black mt-1">{v}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Score Gauge — INDIVIDUALS ONLY */}
          {isIndividual && (
            <Card className="rounded-sm border-ec-line lg:col-span-5">
              <CardHeader className="border-b border-ec-line">
                <CardTitle className="font-display flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-ec-emerald" /> Credibility Score
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center py-10">
                {score ? (
                  <CredibilityGauge score={score.score} tier={score.tier} />
                ) : (
                  <div className="h-[260px] flex items-center font-mono-plex eyebrow">Loading…</div>
                )}
                <Link to="/passport" className="mt-6 text-sm font-semibold text-ec-emerald flex items-center gap-1 hover:underline" data-testid="boost-score-link">
                  Add records to boost your score <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Score Breakdown — INDIVIDUALS ONLY */}
          {isIndividual && (
            <Card className="rounded-sm border-ec-line lg:col-span-7">
              <CardHeader className="border-b border-ec-line">
                <CardTitle className="font-display">Score breakdown</CardTitle>
              </CardHeader>
              <CardContent className="py-6">
                {score ? <ScoreBreakdown factors={score.factors} /> : <div className="eyebrow">Loading…</div>}
              </CardContent>
            </Card>
          )}

          {/* Completion / Badges / Timeline — INDIVIDUALS ONLY */}
          {isIndividual && (
            <>
              <div className="lg:col-span-7">
                <PassportCompletion completion={completion} />
              </div>
              <Card className="rounded-sm border-ec-line lg:col-span-5">
                <CardHeader className="border-b border-ec-line">
                  <CardTitle className="font-display">Professional badges</CardTitle>
                </CardHeader>
                <CardContent className="py-6">
                  <BadgeGrid badges={passport?.badges || []} />
                  <p className="text-xs text-ec-ink-soft mt-4 font-mono-plex">
                    Earn badges by verifying experience, mentoring, training & more.
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-sm border-ec-line lg:col-span-12">
                <CardHeader className="border-b border-ec-line">
                  <CardTitle className="font-display">Career timeline</CardTitle>
                </CardHeader>
                <CardContent className="py-8">
                  <Timeline items={passport?.timeline || []} />
                </CardContent>
              </Card>
            </>
          )}

          {/* Org/Institution issuer-only banner (no score/passport) */}
          {!isIndividual && (
            <Card className="rounded-sm border-ec-line lg:col-span-12 bg-ec-soft">
              <CardContent className="py-8 px-8">
                <div className="eyebrow">Issuer account</div>
                <h3 className="font-display text-2xl font-black tracking-tight mt-2">
                  {user?.user_type === "institution" ? "You are an Institution issuer." : "You are an Organization issuer."}
                </h3>
                <p className="text-ec-ink-soft mt-3 max-w-2xl text-sm leading-relaxed">
                  EthicCred Scores, Trust Tiers and Public Passports are reserved for individual
                  professionals — students, employees, freelancers, gig workers and job seekers.
                  Your {user?.user_type} account is for issuing verified credentials, managing
                  your {user?.user_type === "institution" ? "students" : "workforce"}, and
                  responding to verification requests.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/org-profile"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="open-org-profile-link"><Building2 className="w-4 h-4" /> Complete profile</Button></Link>
                  <Link to={user?.user_type === "institution" ? "/workspace/students" : "/workspace/employees"}><Button variant="outline" className="rounded-sm border-ec-ink gap-2" data-testid="open-workspace-link">{user?.user_type === "institution" ? "Student Directory" : "Employee Directory"}</Button></Link>
                  <Link to="/billing"><Button variant="ghost" className="rounded-sm gap-2"><Sparkles className="w-4 h-4" /> Plan & coupons</Button></Link>
                  <Link to="/certificates"><Button variant="ghost" className="rounded-sm gap-2"><FilePlus2 className="w-4 h-4" /> Issue certificate</Button></Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Issuer card */}
          {isOrg && (
            <Card className="rounded-sm border-ec-line lg:col-span-12">
              <CardHeader className="border-b border-ec-line flex flex-row items-center justify-between space-y-0">
                <CardTitle className="font-display flex items-center gap-2">
                  <FilePlus2 className="w-4 h-4 text-ec-emerald" /> {user?.role === "institution" ? "Institution" : "HR"} workspace
                </CardTitle>
                <div className="flex gap-2">
                  <Link to={user?.role === "institution" ? "/workspace/students" : "/workspace/employees"} data-testid="hr-dashboard-link">
                    <Button variant="outline" className="rounded-sm border-ec-ink gap-2"><Building2 className="w-4 h-4" /> {user?.role === "institution" ? "Students" : "Employees"}</Button>
                  </Link>
                  <Link to="/certificates" data-testid="issue-cert-link">
                    <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">Issue Certificate <ArrowUpRight className="w-4 h-4" /></Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="py-8">
                <p className="text-ec-ink-soft max-w-2xl">
                  As an {user?.role}, you can issue Experience, Conduct (with category ratings), Internship, Training,
                  Volunteer, Recommendation and Achievement certificates directly to any EthicCred
                  ID. Every certificate gets a permanent verification URL and QR code.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
